import React, { useRef, useEffect, forwardRef, useImperativeHandle, useMemo } from 'react';
import { isEqual } from 'lodash';
import { useSelector } from 'react-redux';
import classNames from 'classnames';

import Environment from 'StorytellerEnvironment';
import { getGlobalFilters, getParameterOverrides } from 'store/selectors/DataSourceSelectors';
import { selectors as storySelectors } from 'store/selectors/StorySelectors';
import { selectors as actionComponentSelectors } from 'store/selectors/ActionComponentSelectors';
import type { StorytellerState } from 'store/StorytellerReduxState';

import { JQueryPlugin } from 'types';

import { ComponentResizable } from './ComponentResizable';
import { ComponentEditControls } from 'editor/components/ComponentEditControls';
import { isFlexibleStory } from 'lib/FlexibleLayoutUtils';
import { runJQueryPlugin } from 'editor/renderers/SharedRenderingUtils';

import {
  getComponentRenderer,
  getResizableComponentProps,
} from 'lib/components/block-component-renderers/shared/BlockComponentRendererReactLiaison';
import { isMarginTopPluginType } from './cssComponentLists';
import { getReactRenderer } from './ComponentRegistry';

interface ComponentBaseProps {
  blockId: string;
  componentIndex: number;
  containerClasses?: string[];
  // This is only used for view mode
  editMode: boolean;
}

export const ComponentBase = forwardRef<HTMLDivElement, ComponentBaseProps>(
  ({ blockId, componentIndex, containerClasses = [], editMode }, ref) => {
    const componentRef = useRef<HTMLDivElement>(null);

    useImperativeHandle(ref, () => componentRef.current!, []);

    const theme = useSelector((state: StorytellerState) =>
      storySelectors.getStoryTheme(state, Environment.STORY_UID!),
    );
    const additionalFilters = useSelector(getGlobalFilters, isEqual);
    const parameterOverrides = useSelector(getParameterOverrides, isEqual);
    const componentData = useSelector((state: StorytellerState) => {
      return storySelectors.getBlockComponentAtIndex(state, blockId, componentIndex);
    }, isEqual);

    useSelector(actionComponentSelectors.isActionComponentStoreActive);

    const jQueryRendererToRun = getComponentRenderer(componentData.type);
    const currentRenderer = useRef<JQueryPlugin | null>(null);

    // Check if there's a React renderer for this component type
    const ReactRenderer = getReactRenderer(componentData.type);

    const jQueryResizableProps = getResizableComponentProps(jQueryRendererToRun, componentData);
    // When a componentHero gets an image, it suddenly becomes resizable and that messes with the DOM.
    // CBR-TODO: This is starting to feel too coupled. Not sure how to solve it yet,
    // but just acknowledging the code smell.
    const currentResizeSupported = useRef(jQueryResizableProps.resizeSupported);

    const componentEditControlsProps = {
      blockId,
      componentIndex,
      componentData,
      editMode,
    };

    const pluginProps = useMemo(
      () => ({
        componentData,
        theme,
        editMode,
        blockId,
        componentIndex,
        additionalFilters,
        parameterOverrides,
      }),
      [componentData, theme, editMode, blockId, componentIndex, additionalFilters, parameterOverrides],
    );

    // render the jQuery plugin on the ref (skip if using React renderer)
    useEffect(() => {
      // Skip if we're using a React renderer instead of jQuery
      if (ReactRenderer) {
        return;
      }

      if (componentRef?.current) {
        // Skip asset selectors in view mode
        if (!editMode && jQueryRendererToRun === JQueryPlugin.ASSET_SELECTOR) {
          return;
        }

        const needToChangeRenderer =
          currentRenderer.current !== jQueryRendererToRun ||
          currentResizeSupported.current !== jQueryResizableProps.resizeSupported; // For hero components
        if (needToChangeRenderer) {
          currentRenderer.current = jQueryRendererToRun;
          currentResizeSupported.current = jQueryResizableProps.resizeSupported;
        }
        runJQueryPlugin(componentRef.current, needToChangeRenderer, jQueryRendererToRun, pluginProps);
      }
      // No skipping renders. This will run any time componentData changes, including every keypress
      // According to my current performance testing, this is no longer slow.
    }, [ReactRenderer, jQueryRendererToRun, pluginProps, editMode, jQueryResizableProps]);

    const renderComponentContainer = () => {
      // If there's a React renderer, use it directly instead of jQuery, otherwise,
      // render empty container for jQuery plugin to fill
      return (
        <div
          className={'component-container'}
          ref={componentRef}
          data-component-index={componentIndex}
          {...(!editMode && componentData?.componentUuid
            ? { 'data-component-uuid': componentData?.componentUuid }
            : {})}
        >
          {ReactRenderer && <ReactRenderer {...pluginProps} />}
        </div>
      );
    };

    const renderResizableComponentContainer = () => {
      return (
        <ComponentResizable
          ref={componentRef}
          componentData={componentData}
          blockId={blockId}
          componentIndex={componentIndex}
          editMode={editMode}
        />
      );
    };

    return (
      <div
        className={classNames(
          'component-base-container',
          `jq-renderer-${jQueryRendererToRun}`,
          { 'dynamic-margin-top': isMarginTopPluginType(jQueryRendererToRun) },
          ...containerClasses,
        )}
      >
        <ComponentEditControls {...componentEditControlsProps} />
        {!isFlexibleStory() ? renderResizableComponentContainer() : renderComponentContainer()}
      </div>
    );
  },
);
